import { useEffect, useState } from 'react';
import { startCase } from 'lodash';

import { Tab, TabList, useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import type { IntersectingElement } from '@/pages/Narrative/components/NarrativeIntersectionObserver';
import { elements } from '@/pages/Narrative/components/NarrativeIntersectionObserver';
import {
  StyledLink,
  StyledNav,
} from '@/pages/Narrative/components/Navigation/styled';
import CommentCount from '@/pages/Narrative/components/NarrativeComment/CommentCount';
import { useNarrativeSlug } from '@/pages/Narrative/hooks';

export type Props = {
  intersectingElement: IntersectingElement;
  setIntersectingElement: (element: IntersectingElement) => void;
};

export const Navigation = ({
  intersectingElement,
  setIntersectingElement,
}: Props) => {
  const theme = useTheme();
  const slug = useNarrativeSlug();
  const { isDesktop } = useUserAgent();
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const handleOnClick = (intersectingElement: IntersectingElement) => {
    return () => {
      if (typeof window !== 'undefined') {
        const element = window.document.querySelector(
          `#${intersectingElement}`
        ) as HTMLElement;
        if (element) {
          window.scrollTo({
            top: isDesktop ? element.offsetTop - 32 : element.offsetTop - 56,
          });
        }
      }
    };
  };

  const scrollToTab = (anchor: string) => {
    const tabList = window.document.querySelector('#narrative-tab-list');
    const element = window.document.getElementById(
      `#narrative-tab-item-${anchor}`
    );

    if (tabList && element) {
      tabList.scrollTo({
        top: 0,
        left: element.offsetLeft,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const locationHash = window.location.hash.slice(1) as IntersectingElement;
    if (
      !hasLoaded &&
      elements.includes(locationHash) &&
      locationHash !== 'key-takeaways'
    ) {
      if (typeof window !== 'undefined') {
        const element = window.document.querySelector(
          `#${locationHash}`
        ) as HTMLElement;
        if (element) {
          window.scrollTo({
            top: isDesktop ? element.offsetTop - 32 : element.offsetTop - 56,
          });
        }
      }
    } else {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0 });
      }
    }
    setHasLoaded(true);
  }, [setIntersectingElement]);

  useEffect(() => {
    if (hasLoaded && elements.includes(intersectingElement)) {
      scrollToTab(intersectingElement);
      window.history.replaceState(null, '', `#${intersectingElement}`);
    }
  }, [intersectingElement]);

  const isActiveTab = (element: IntersectingElement) => {
    if (element === 'key-takeaways') {
      return (
        intersectingElement === element ||
        intersectingElement === 'author-valuation'
      );
    }

    return intersectingElement === element;
  };

  return (
    <StyledNav id="narrative-tab-list">
      <TabList
        templateColumns={`repeat(6, max-content)`}
        columnGap={`${theme.x1spacePx}px`}
        $width="max-content"
      >
        {elements.map((element) => {
          return (
            <StyledLink
              key={element}
              href={`#${element}`}
              id={`#narrative-tab-item-${element}`}
              onClick={(e) => e.preventDefault()}
            >
              <Tab
                styleType="subnav"
                selected={isActiveTab(element)}
                key={element}
                value={element}
                onClick={handleOnClick(element)}
              >
                {startCase(element)}{' '}
                {element === 'comments' && (
                  <CommentCount canonicalUrl={`/narratives/${slug}`} />
                )}
              </Tab>
            </StyledLink>
          );
        })}
      </TabList>
    </StyledNav>
  );
};
